<template>
  <v-card class="mb-6 whatsapp-file-upload">
    <v-card-text>
      <div class="d-flex flex-column" style="gap: 18px">
        <span class="mf-subtitle">Escolha uma imagem ou arquivo em PDF</span>
        <div class="d-flex flex-row align-center" style="gap: 14px">
          <mf-button :disabled="disabled" :loading="loading" label="Selecionar" @click="activateInput" />
          <span v-if="fileProperties && fileProperties.filename" class="file-chip">
            <mf-icon icon="photo" size="24" fill color="marca-0-l40" />
            {{ fileProperties.filename }}
            <mf-icon class="close-icon" size="24" @click="deleteFile">close</mf-icon>
          </span>
        </div>
      </div>

      <canvas id="the-canvas" style="display: none" />
      <p class="hint-text mt-4 mb-0">
        Os formatos aceitos: <span class="marca-1-d20-text">PDF, PNG, JPG ou JPEG</span>. O tamanho máximo é de <span class="marca-1-d20-text">até 10 MB</span>.
      </p>
      <input ref="file-input" class="hidden-input" type="file" @input="readFile($event)" />
    </v-card-text>
  </v-card>
</template>

<script>
import Swal from 'sweetalert2'
import pdfjs from 'pdfjs-dist/webpack'
import gql from 'graphql-tag'
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'

const MUTATION_SINGLE_UPLOAD = gql`
  mutation SingleUpload($file: Upload!) {
    singleUpload(file: $file) {
      filename
      mimetype
      url
    }
  }
`

export default {
  name: 'FileUploadCard',
  props: {
    fileProperties: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    file: null,
    fileName: '',
    fileExtension: '',
    loading: false,
    allowedFileTypes: ['pdf', 'png', 'jpg', 'jpeg'],
  }),
  methods: {
    activateInput() {
      this.$refs['file-input'].value = null
      this.$refs['file-input'].click()
    },
    deleteFile() {
      this.file = null
      this.fileName = ''
      this.fileExtension = ''
      this.$emit('update:fileProperties', {})
    },
    validFormatType(file) {
      return this.allowedFileTypes.includes(file.name.split('.').pop().toLowerCase())
    },
    readFile(event) {
      if (event.target.files.length === 0) return
      this.file = event.target.files[0]

      if (this.file.size > 10 * 1024 * 1024) {
        Swal.fire({
          text: 'Arquivo com mais de 10MB.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1800,
          onAfterClose: this.goToFocusElement,
          customClass: {
            content: 'content-class',
          },
        })
        this.deleteFile()
      } else if (!this.validFormatType(this.file)) {
        Swal.fire({
          text: 'Formato de arquivo inválido.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1800,
          onAfterClose: this.goToFocusElement,
          customClass: {
            content: 'content-class',
          },
        })
        this.deleteFile()
      } else {
        this.fileName = this.file.name
        this.fileExtension = this.file.name.split('.').pop()

        if (this.fileExtension.toLowerCase() === 'pdf') {
          this.pdfUpload({ file: this.file, originalFilename: this.fileName })
        } else {
          this.imageUpload({ file: this.file, originalFilename: this.fileName })
        }
      }
    },
    async pdfUpload({ file, originalFilename }) {
      try {
        this.loading = true
        const base64 = await this.getBase64ImagemFromPdf(this.file)

        const { url: thumbnail_url } = await this.uploadFileBase64(originalFilename.replace('pdf', 'jpg'), base64)
        const { filename, mimetype, url: data_url } = await this.uploadFile(file)

        this.$emit('update:fileProperties', { filename, mimetype, thumbnail_url, data_url })
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    async imageUpload({ file, originalFilename }) {
      try {
        this.loading = true
        const { filename, mimetype, url } = await this.uploadFile(this.file)

        this.$emit('update:fileProperties', { filename, mimetype, thumbnail_url: url, data_url: url })
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    uploadFile(file) {
      return this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD,
          variables: { file: this.file },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUpload } }) => {
          const { filename, mimetype, url } = singleUpload
          return { filename, mimetype, url }
        })
    },
    uploadFileBase64(filename, base64) {
      return this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: {
            filename,
            base64,
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { filename, mimetype, url } = singleUploadBase64
          return { filename, mimetype, url }
        })
    },
    goToFocusElement() {
      const position = this.$refs['file-input'].parentElement.getBoundingClientRect()
      window.scrollTo(0, parseInt(position.top) - 250)
    },
    getBase64ImagemFromPdf(file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader()

        fileReader.onload = async function () {
          const typedarray = new Uint8Array(this.result)
          const pdf = await pdfjs.getDocument(typedarray).promise
          const page = await pdf.getPage(1)
          const scale = 1.5
          const viewport = page.getViewport({ scale })
          // Prepare canvas using PDF page dimensions
          const canvas = document.getElementById('the-canvas')
          const context = canvas.getContext('2d')
          canvas.height = viewport.height
          canvas.width = viewport.width
          // Render PDF page into canvas context
          const task = page.render({ canvasContext: context, viewport: viewport })
          await task.promise
          resolve(canvas.toDataURL('image/jpeg'))
        }
        fileReader.readAsArrayBuffer(file)
      })
    },
  },
}
</script>

<style lang="scss">
.whatsapp-file-upload {
  .hidden-input {
    display: none;
  }

  .hint-text {
    font-size: 12px;
    letter-spacing: 0.23px;
    color: $cinza-0;
  }

  .file-chip {
    user-select: none;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    .close-icon {
      cursor: pointer;
    }
  }

  .marca-1-d20-text {
    font-weight: bold;
    color: $marca-1-d20;
  }
}
</style>
